import { Injectable, Injector } from "@angular/core";
import { ClientLogProvider } from "../../api/client-log/client-log.api";
import { AppConfig } from "../../app.config";
import { Auth } from "../auth/auth.service";
import { AngularFireProvider } from "../firebase/angularfire.service";
import { ViewActionTrackerService } from "../tracker/view-action-logger.service";
import {
  buildErrorContext,
  ErrorContext,
  findBlankModalSymptom,
  stripQueryString,
} from "./client-diagnostics";

const MAX_CRUMBS = 8;
// 描画途中を空と誤判定しないよう、表示完了からこの時間待って中身を確認する
const RENDER_CHECK_DELAY_MS = 3000;
// 同じ端末で空モーダルが連発してもログが溢れないようにする
const MAX_RENDER_FAILURE_REPORTS = 3;
// GA4 のイベントパラメータ値の上限(100文字)
const GA4_PARAM_MAX_LENGTH = 100;

/**
 * 画面の表示失敗を調査するための診断情報を集める。
 * - 直近の操作(view_action_log)を覚えておき、例外ログに文脈として添える
 * - 表示から数秒経っても中身が空のモーダルを検出し、render_failed として記録する
 * 記録先は New Relic(ClientErrorLog) と GA4(client_error イベント、BigQuery で user_id と突合可能)。
 */
@Injectable({ providedIn: "root" })
export class ClientDiagnosticsService {
  private crumbs: string[] = [];
  private renderFailureReports = 0;
  private started = false;

  constructor(
    private viewActionService: ViewActionTrackerService,
    private injector: Injector
  ) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.viewActionService.viewAction$.subscribe(
      ({ screen_name, action_name }) => {
        this.crumbs.push(`${screen_name}:${action_name}`);
        if (this.crumbs.length > MAX_CRUMBS) {
          this.crumbs.shift();
        }
      }
    );
    document.addEventListener("ionModalDidPresent", (event) => {
      const modal = event.target as Element | null;
      if (modal) {
        setTimeout(() => this.checkModalRendered(modal), RENDER_CHECK_DELAY_MS);
      }
    });
  }

  buildContext(): ErrorContext | null {
    try {
      return buildErrorContext(document, this.crumbs);
    } catch {
      return null;
    }
  }

  /** 例外ログ(GlobalErrorHandler)を GA4 にも残す */
  logErrorToAnalytics(message: string, context: ErrorContext | null): void {
    this.logToAnalytics({
      error_type: "exception",
      message: stripQueryString(message),
      modal: context?.modal?.tag ?? "",
      modal_kind: String(context?.modal?.props.kind ?? ""),
      route: context?.route ?? "",
      host: context?.host ?? "",
    });
  }

  private checkModalRendered(modal: Element): void {
    try {
      // 既に閉じられた・裏に回ったモーダルは対象外
      if (!modal.isConnected || modal.classList.contains("overlay-hidden")) {
        return;
      }
      if (this.renderFailureReports >= MAX_RENDER_FAILURE_REPORTS) {
        return;
      }
      const symptom = findBlankModalSymptom(modal);
      if (!symptom) {
        return;
      }
      this.renderFailureReports++;
      const context = this.buildContext();
      const message = `render_failed: ${symptom.tag || "unknown"}`;

      if (AppConfig.ENV !== "production") {
        console.warn("[client-diagnostics]", message, symptom, context);
        return;
      }
      this.injector.get(ClientLogProvider).post({
        user_id: this.injector.get(Auth).getMe()?.id ?? "",
        message: message,
        url: context?.route ?? "",
        path: stripQueryString(document.URL),
        stack: JSON.stringify(symptom),
        context: context ? JSON.stringify(context) : "",
      });
      this.logToAnalytics({
        error_type: "render_failed",
        message: message,
        modal: symptom.tag,
        modal_kind: String(symptom.props.kind ?? ""),
        route: context?.route ?? "",
        host: context?.host ?? "",
      });
    } catch {
      // 診断の失敗で画面の動作を妨げない
    }
  }

  private logToAnalytics(params: { [key: string]: string }): void {
    try {
      const truncated: { [key: string]: string } = {};
      for (const [key, value] of Object.entries(params)) {
        truncated[key] = value.slice(0, GA4_PARAM_MAX_LENGTH);
      }
      this.injector
        .get(AngularFireProvider)
        .logEvent("client_error", truncated);
    } catch {
      // 診断の失敗で画面の動作を妨げない
    }
  }
}
